import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../../components/Store";
import "./HomeScreen.css";
import { Context2 } from "../../components/Audio";
import { FaVolumeUp, FaVolumeMute } from "react-icons/fa";
import { useBootstrap } from "@/features/bootstrap";
import PracticeIntroModal from "./components/PracticeIntroModal";
import { useAppAccess } from "@/features/access-control";
import { ROLES } from "@/features/router/roles";

const DAY_STREAKS = [
  3, 5, 7, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 125, 150, 175, 200, 250,
  300, 365, 500, 730, 1095, 1460, 1825,
];

const HomeScreen = () => {
  const { data: user } = useBootstrap();
  const [count, setCount] = useState(0);
  const [studentName, setStudentName] = useState("");
  const [showPracticeIntroModal, setShowPracticeIntroModal] = useState(false);
  const navigate = useNavigate();
  const [, setWellnessDataPoint] = useContext(Context);
  const [playing, setPlaying] = useContext(Context2);
  const [loadingData, setLoadingData] = useState(true);
  const { isPaused } = useAppAccess();

  const isStaff = user.role !== ROLES.STUDENT;
  const studentIsPaused = !isStaff && isPaused;

  const numberWithCommas = (x) => {
    return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
  };

  const getUserInfo = useCallback(async () => {
    const nameCandidate =
      (user && user?.displayName) ??
      (user && user.email) ??
      "to 1-2-3 Wellness";

    const firstName =
      typeof nameCandidate === "string" && nameCandidate.length > 0
        ? nameCandidate.split(" ")[0]
        : (user && user.email) || "";

    setStudentName(firstName);
    setCount(user?.count);
  }, [user]);

  useEffect(() => {
    getUserInfo().finally(() => setLoadingData(false));
  }, [getUserInfo]);

  useEffect(() => {
    const splitRef = window.location.href.split("?");
    if (splitRef.length > 1) navigate("/");
  }, []);

  const totalPracticeCount = typeof user?.count === "number" ? user.count : 0;
  const shouldShowPracticeIntroModal = totalPracticeCount <= 3;
  const displayedCastleBricks =
    totalPracticeCount === 0 ? 0 : ((totalPracticeCount - 1) % 60) + 1;
  const littleCastleCount = Math.floor(
    Math.max(totalPracticeCount - 1, 0) / 60,
  );

  function clearDataPointAndNav() {
    setWellnessDataPoint(initialState);
    navigate("/deepbreathe");
  }

  function handleStartPractice() {
    if (shouldShowPracticeIntroModal) {
      setShowPracticeIntroModal(true);
      return;
    }
    clearDataPointAndNav();
  }

  function closePracticeIntroModal() {
    setShowPracticeIntroModal(false);
  }

  function startPracticeFromModal() {
    closePracticeIntroModal();
    clearDataPointAndNav();
  }

  async function changeAudio(newPlaying) {
    document.cookie = `audio=${newPlaying}; path=/; SameSite=Strict; Secure`;
    setPlaying(newPlaying);
  }

  const VolumeButton = () => (
    <div
      className="volume-home"
      style={
        studentIsPaused
          ? { color: "#9ca3af", cursor: "not-allowed" }
          : undefined
      }
    >
      {playing ? (
        <div
          className="volume-up"
          style={studentIsPaused ? { cursor: "not-allowed" } : undefined}
          onClick={!studentIsPaused ? () => changeAudio(false) : undefined}
        >
          <div className="volume-home-icon">
            <FaVolumeUp />
          </div>
        </div>
      ) : (
        <div
          className="volume-mute"
          style={studentIsPaused ? { cursor: "not-allowed" } : undefined}
          onClick={!studentIsPaused ? () => changeAudio(true) : undefined}
        >
          <div className="volume-home-icon">
            <FaVolumeMute />
          </div>
        </div>
      )}
    </div>
  );

  const LittleCastles = () => (
    <div className="littlecastlerow">
      {Array.from({ length: littleCastleCount }, (_, idx) => (
        <img
          key={idx}
          className="littlecastle"
          src={"../images/Castle_Gifs/Castle(60).gif"}
          alt="loading littl..."
          loading="lazy"
        />
      ))}
    </div>
  );

  // Welly the mascot sits in the top-left corner of the welcome area on
  // tablets and computers, and above the castle / GIF on phones.
  const Welly = () => (
    <img
      className="wellyCorner"
      src="../images/mascot/welly-hello.webp"
      alt="Welly the kangaroo waving and saying Hello, I'm Welly! Welcome to 1-2-3 Wellness!"
    />
  );

  const CastleDisplay = () => (
    <div className="buildcastle" data-onboarding-id="home-welcome">
      {!loadingData && <div className="welcome">Welcome, {studentName}!</div>}
      <Welly />
      {displayedCastleBricks === 60 ? (
        <img
          className="castle"
          src={"../images/Castle_Gifs/Castle(60).gif"}
          alt="loading larger..."
          loading="lazy"
        />
      ) : (
        <img
          className="castlebigger"
          src={`../images/Castle_Gifs/Castle(${displayedCastleBricks}).gif`}
          alt="loading..."
          loading="lazy"
        />
      )}
      {(!user?.count || count === 0) && (
        <h1 className="hometext">
          Time to get started on your wellness journey!
        </h1>
      )}
      {count > 0 && (
        <h1 className="hometext">
          You have practiced wellness{" "}
          <strong>{numberWithCommas(user?.count ?? 0)} </strong>
          {typeof user?.count === "number" &&
            (user?.count <= 1 ? "time" : "times")}
          ! Great work!
        </h1>
      )}
      {!studentIsPaused && (
        <button
          onClick={handleStartPractice}
          className="wellnessButtons"
          data-onboarding-id="home-start"
        >
          START
        </button>
      )}
      {littleCastleCount > 0 && <LittleCastles />}
    </div>
  );

  const MaxBadge = (props) => {
    if (
      !props.badges ||
      Object.values(props.badges).every((count) => count === 0)
    ) {
      return (
        <div className="homePageAchievementImageItem">
          <img
            src="../images/badges/color/BlankBadgeTrophyColor.png"
            alt="Default Trophy Badge"
            className="homePageAchievementDisplayImage"
            id="homePageBadge"
          />
          <p className="homePageAchievementImageLabel">...</p>
        </div>
      );
    }

    let maxBadge = null;
    let maxCount = -1;

    for (const [badge, count] of Object.entries(props.badges)) {
      if (count > maxCount && count > 0) {
        maxBadge = badge;
        maxCount = count;
      }
    }

    if (maxBadge) {
      let capitalizedBadge =
        maxBadge.charAt(0).toUpperCase() + maxBadge.slice(1);
      let badgeFileName = `${capitalizedBadge}BadgeColor.png`;

      return (
        <div className="homePageAchievementImageItem">
          <img
            src={`../images/badges/color/${badgeFileName}`}
            alt={`${capitalizedBadge} Badge`}
            className="homePageAchievementDisplayImage"
            id="homePageBadge"
          />
          <p className="homePageAchievementImageLabel">
            {capitalizedBadge} {maxCount > 1 ? maxCount + " x" : ""}
          </p>
        </div>
      );
    }

    return (
      <div className="homePageAchievementImageItem">
        <img
          src="../images/badges/color/BlankBadgeTrophyColor.png"
          alt="Default Trophy Badge"
          className="homePageAchievementDisplayImage"
          id="homePageBadge"
        />
        <p className="homePageAchievementImageLabel">...</p>
      </div>
    );
  };

  const MaxStreak = (props) => {
    const isDayStreak = props.maxDayStreak !== undefined;

    const getNearestStreakValue = () => {
      const currMax = props.maxDayStreak;
      const streak_arr = DAY_STREAKS;
      if (currMax < streak_arr[0]) return 0;
      for (let i = 1; i < streak_arr.length; i++) {
        const streakVal = streak_arr[i];
        if (currMax === streakVal) return streakVal;
        else if (currMax < streakVal) return streak_arr[i - 1];
      }
      return streak_arr[streak_arr.length - 1];
    };

    const nearestStreakVal = getNearestStreakValue();
    if (isDayStreak && nearestStreakVal > 0) {
      return (
        <div className="homePageAchievementImageItem">
          <img
            className="homePageAchievementDisplayImage"
            src={`../images/streaks/dayStreaks/Color/${nearestStreakVal}DaysColor.png`}
            id="homePageStreak"
            alt="day-streaks"
          />
          <p className="homePageAchievementImageLabel">
            Max Day Streak: {props.maxDayStreak}
          </p>
        </div>
      );
    }
    return (
      <div className="homePageAchievementImageItem">
        <img
          className="homePageAchievementDisplayImage"
          src={`../images/streaks/genericStreaks/BlankStreakBluecolor.png`}
          id="homePageStreak"
          alt="generic-streaks"
        />
        <p className="homePageAchievementImageLabel">...</p>
      </div>
    );
  };

  const CoinCount = (props) => (
    <div className="homePageAchievementImageItem">
      <img
        className="homePageAchievementDisplayImage"
        src="../images/coin front.png"
        id="homePageCoin"
        alt="achievement display"
      />
      <p className="homePageAchievementImageLabel">
        {props.coins && props.coins > 0
          ? `${props.coins} ${props.coins === 1 ? "coin" : "coins"}`
          : "..."}
      </p>
    </div>
  );

  const DefaultHomeScreenElement = () => (
    <div>
      <div className="homepageTopDiv" data-onboarding-id="home-welcome">
        <div className="blueBackgroundSubtitleHome">
          <h3 className="blueBackgroundSubtitleHeaderHome">
            Welcome, {studentName}!
          </h3>
          <h3 className="blueBackgroundSubtitleHeaderHome2">
            Build wellness in your heart and mind and your actions will change
            the world.{" "}
          </h3>
        </div>
        <div id="homepageTopDivBody">
          <Welly />
          <img
            id="heartMindWellnessGif"
            src="../images/Heart_Mind_World_123Wellness_GIF/123WellnessGIF-2024.gif"
            alt="mind wellness gif"
          />
          {!studentIsPaused && (
            <div className="button-wrapper">
              <button
                onClick={handleStartPractice}
                id="homepageTopDivWellnessButton"
                data-onboarding-id="home-start"
              >
                START
              </button>
            </div>
          )}
          {!studentIsPaused && <VolumeButton />}
        </div>
      </div>
      <div className="homepageBottomDiv">
        <h2 id="yourAchievementsHeader">Your Achievements</h2>
        {(count === null || count === 0) && (
          <p id="practiceEncouragementParagraph">
            Start practicing wellness and watch your achievements grow!{" "}
          </p>
        )}
        {count > 0 && (
          <h1 id="practiceEncouragementParagraph">
            <strong>Congratulations,</strong> you've practiced wellness{" "}
            <strong>
              {numberWithCommas(count)} {count === 1 ? "time" : "times"}!
            </strong>
          </h1>
        )}
        <div id="achievementDisplayGrid">
          <CoinCount coins={user?.coins} />
          <MaxStreak maxDayStreak={user?.maxDayStreak} />
          <MaxBadge badges={user?.badges} />
        </div>
      </div>
    </div>
  );

  const ActiveProgressTracker = () =>
    user?.usesCastle ? <CastleDisplay /> : <DefaultHomeScreenElement />;

  return (
    <div className="outermostrow">
      <ActiveProgressTracker />
      <PracticeIntroModal
        isOpen={showPracticeIntroModal}
        onClose={closePracticeIntroModal}
        onStart={startPracticeFromModal}
      />
    </div>
  );
};

const initialState = {
  action: "",
  emotion: "",
  date: "",
  helpful: false,
};

export default HomeScreen;
